import { Directive, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[formControlFile]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormControlFileDirective),
      multi: true,
    },
  ],
})
export class FormControlFileDirective implements ControlValueAccessor {
  private _value: File | any = null;
  private onChange = (_: any) => {};
  private onTouched = () => {};

  /** MB */
  @Input() maxSize: number = 5;
  /** Tipos permitidos. Ej: ['png', 'jpg', 'pdf'] */
  @Input() allowedTypes: string[] = [];

  @Output() prevUUID = new EventEmitter<string>();
  @Output() onExcedeMaximo = new EventEmitter<void>();
  @Output() onTipoInvalido = new EventEmitter<void>();

  @HostBinding('attr.filename') get filename() {
    return this._value?.realName || this._value?.name || this.host?.nativeElement?.placeholder;
  }

  @HostBinding('class.filled') get filled() {
    return !!this._value;
  }

  constructor(private host: ElementRef<HTMLInputElement>) {}

  @HostListener('change', ['$event.target.files'])
  handleFileChange(files: FileList | null) {
    let file = files && files.length > 0 ? files[0] : null;

    if (this.excedeMaximo(file)) {
      this.onExcedeMaximo.emit();
      file = null;
    }

    if (this.tipoInvalido(file)) {
      this.onTipoInvalido.emit();
      file = null;
    }

    if (this._value?.uuid) this.prevUUID.emit(this._value.uuid);

    this.writeValue(file);
    this.onChange(file);
    this.onTouched();
  }

  excedeMaximo(file: File) {
    const mb = file.size / 1024 / 1024;
    return mb > this.maxSize;
  }

  tipoInvalido(file: File) {
    if (!file || this.allowedTypes.length === 0) return false;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    return !this.allowedTypes.some(type => {
      const normalizedType = type.toLowerCase().replace('image/', '').replace('application/', '');
      return fileType.includes(normalizedType) || fileExtension === normalizedType;
    });
  }

  writeValue(value: any): void {
    if (value === null) {
      this.host.nativeElement.value = '';
      this._value = null;
    } else {
      this._value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.host.nativeElement.disabled = isDisabled;
  }
}
